import { GridActionsComponent, GridCheckboxButtonComponent } from '@mds/ui';
import { numericComparator } from '@utils/numeric-comparator';
import { sortBy } from '@utils/sort-by';
import { textComparator } from '@utils/text-comparator';
import { ColDef, ValueSetterParams } from 'ag-grid-enterprise';

import { AutocompleteCellEditorComponent } from '../autocomplete-cell-editor/autocomplete-cell-editor.component';
import {
  DataRow,
  EDITABLE_CELL_STYLE,
  FormSchemaItem,
  LINK_CELL_STYLE,
  NO_EDIT_FIELDS,
  NO_FILTER_TEXT_FIELDS,
  NUMERIC_FIELD_TYPES,
  YIELD_FACTOR_FIELD
} from './composer-grid.models';

function objValueFormatter(params: any): string {
  if (!params.value) return '';
  return params.value.title || params.value.name || params.value;
}

function buildColDef(row: DataRow, formSchema: FormSchemaItem[] | undefined, isEditable: boolean): ColDef {
  const rawKey = row.key.replace('$', '.');
  const field = rawKey.split('.').pop()!;

  const col: ColDef = {
    field,
    headerName: row.label,
    comparator: textComparator,
    minWidth: 150,
    sortable: false,
    filter: 'agTextColumnFilter',
    enableRowGroup: row.label !== 'Main Output',
    floatingFilterComponentParams: { suppressFilterButton: true },
    suppressHeaderFilterButton: true,
  };

  if (field === 'is_private') {
    col.cellRenderer = 'TogglePrivateRendererComponent';
    delete col.filter;
  }

  if (['in', 'out', 'name'].includes(field)) {
    col.enablePivot = true;
    col.pivot = true;
  }

  if (row.label === 'Main Output' && field === 'outputGp') {
    col.cellRenderer = GridCheckboxButtonComponent;
  }

  if (typeof row.value === 'number') {
    col.filter = 'agNumberColumnFilter';
    col.aggFunc = 'sum';
    col.enableValue = true;
    col.comparator = numericComparator;
    col.sortable = true;
  } else if (['string', 'object'].includes(typeof row.value)) {
    if (!NO_FILTER_TEXT_FIELDS.has(field)) {
      col.filter = 'agTextColumnFilter';
    }
    col.sortable = true;
  } else {
    col.sortable = true;
  }

  if (formSchema) {
    applyFormSchema(col, field, formSchema, isEditable);
  }

  if (row.link) {
    const headerName = row.label;
    col.cellStyle = (params) => LINK_CELL_STYLE(params, headerName);
    col.valueFormatter = (params) => {
      const hasLink = params.data?.[`Link_${headerName}`] || params.data?.[`Link_${params.colDef.field}`];
      if (!hasLink && params.value === 'Unknown') {
        return `No ${headerName}`;
      }
      return params.value;
    };
  } else if (col.editable) {
    col.cellStyle = EDITABLE_CELL_STYLE;
  }

  return col;
}

function applyFormSchema(col: ColDef, field: string, formSchema: FormSchemaItem[], isEditable: boolean): void {
  for (const form of formSchema) {
    const formKey = form.key.replace('$', '.').split('.').pop()!;
    if (formKey !== field) continue;

    const isNonEditable = NO_EDIT_FIELDS.has(field) || ['display_only', 'hidden'].includes(form.formtype);

    if (form.formtype === 'hidden') {
      col.hide = true;
      col.editable = false;
      return;
    }

    if (!isNonEditable) {
      col.editable = isEditable;
    }

    if (['select', 'select_boolean'].includes(form.formtype)) {
      col.cellEditorSelector = () => ({
        component: 'agRichSelectCellEditor',
        params: { values: form.enum?.map((e) => e.name) || [] }
      });
    }

    if (form.formtype === 'select_boolean' || form.fieldtype === 'BOOLEAN' || form.enum?.length) {
      const enumEntries = form.enum || [];
      const displayValues = enumEntries.map((e) => e.name);
      const displayToRawMap: Record<string, string> = {};
      for (const e of enumEntries) {
        displayToRawMap[e.name] = String(e.value);
      }
      col.filter = 'agSetColumnFilter';
      col.filterParams = {
        values: displayValues.length ? displayValues : ['Yes', 'No'],
        displayToRawMap: displayValues.length ? displayToRawMap : { Yes: 'true', No: 'false' }
      };
    }

    if (form.formtype === 'checkbox' && col.field !== 'is_private') {
      col.editable = false;
      col.cellRenderer = GridCheckboxButtonComponent;
      col.cellStyle = EDITABLE_CELL_STYLE;
    }

    if (NUMERIC_FIELD_TYPES.has(form.fieldtype)) {
      col.cellEditor = 'agNumberCellEditor';
      col.filter = 'agNumberColumnFilter';
      col.aggFunc = 'sum';
      col.comparator = numericComparator;

      if (field === YIELD_FACTOR_FIELD) {
        col.cellEditorParams = { min: 0, max: 1, precision: 2 };
        col.valueSetter = (params: ValueSetterParams) => {
          if (params.newValue === '' || params.newValue == null) return false;
          const val = Number(params.newValue);
          if (isNaN(val) || val < 0 || val > 1) return false;
          params.data[params.colDef.field!] = val;
          return true;
        };
      }
    } else if (['tile-autocomplete', 'tile-autoselect', 'autocomplete', 'autoselect'].includes(form.formtype)) {
      Object.assign(col, {
        cellEditor: AutocompleteCellEditorComponent,
        cellEditorParams: {
          optionsUrl: form.options || '',
          defaultOptionsUrl: form.default_options || form.options || '',
          defaultOptionKeys: form.default_option_key || [],
          formtype: form.formtype
        },
        valueFormatter: objValueFormatter,
        cellStyle: EDITABLE_CELL_STYLE,
        minWidth: 250
      });
    } else if (form.formtype === 'date') {
      Object.assign(col, {
        cellEditor: 'agDateStringCellEditor',
        cellStyle: EDITABLE_CELL_STYLE
      });
    } else if (form.formtype === 'formula') {
      col.cellEditor = 'agLargeTextCellEditor';
    }
  }
}

function addSelectionAndActionCols(cols: ColDef[], onActionClick: (params: any) => void): void {
  cols.push({
    headerName: 'Actions',
    field: 'actions',
    cellRenderer: GridActionsComponent,
    cellRendererParams: {
      onClick: (params: any) => onActionClick(params)
    },
    width: 140,
    pinned: 'right',
    filter: false,
    sortable: false,
    lockPinned: true,
    lockPosition: 'right',
    suppressSizeToFit: true,
    resizable: false
  });
  cols.push({
    checkboxSelection: true,
    headerCheckboxSelection: true,
    headerCheckboxSelectionFilteredOnly: true,
    pinned: 'left',
    lockPosition: 'right',
    lockPinned: true,
    width: 48,
    filter: false,
    sortable: false,
    suppressSizeToFit: true,
    suppressHeaderMenuButton: true,
    suppressColumnsToolPanel: true
  });
}

export function buildColumnDefs(
  lines: any[],
  formSchema: FormSchemaItem[] | undefined,
  isEditable: boolean,
  onActionClick: (params: any) => void,
  showActions: boolean = isEditable
): ColDef[] {
  const rawHeader = lines[0] || [];
  const ordered = rawHeader.filter((r) => r.sortlevel != null);
  sortBy(ordered);
  const unordered = rawHeader.filter((r) => r.sortlevel == null);
  const headerData = [...ordered, ...unordered];
  const cols = headerData
    .filter((r) => r.key && !r.key.endsWith('.rid'))
    .map((r) => buildColDef(r, formSchema, isEditable));

  if (cols.length && showActions) {
    addSelectionAndActionCols(cols, onActionClick);
  }

  return cols;
}
